import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { kycConfig } from "@/api/adminPanel";
import { Badge, Button, Card, CardBody, CardHeader, FormError, Input, Label, Select } from "@/components/ui";
import { useToast } from "@/components/Toast";

const PROVIDERS = ["persona", "sumsub", "onfido", "veriff", "test"];

export function AdminKycPage() {
  const toast = useToast();
  const cfg = useQuery({ queryKey: ["kyc-config"], queryFn: () => kycConfig.get() });
  const [provider, setProvider] = useState(PROVIDERS[0]);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => kycConfig.setProviderSecret(provider, secret || null),
    onSuccess: () => {
      toast.push(`${provider} secret ${secret ? "saved" : "cleared"}`, "success");
      setSecret("");
      cfg.refetch();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">KYC providers</h1>

      <Card>
        <CardHeader>
          <div>
            <h2 className="text-sm font-semibold">Configured providers</h2>
            <p className="mt-1 text-xs text-slate-500">
              These secrets sign incoming webhooks at{" "}
              <code>POST /api/v1/kyc/webhook/&lt;provider&gt;</code>. Leave empty to remove a provider.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          {cfg.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : Object.keys(cfg.data?.providers ?? {}).length === 0 ? (
            <p className="text-sm text-slate-500">No KYC providers configured for this org yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Provider</th>
                  <th>Secret</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(cfg.data?.providers ?? {}).map(([name, p]) => (
                  <tr key={name} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 font-mono text-xs">{name}</td>
                    <td>
                      {p.secret_set ? (
                        <Badge tone="good">stored</Badge>
                      ) : (
                        <Badge tone="warn">missing</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Set provider secret</h2>
        </CardHeader>
        <CardBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              save.mutate();
            }}
            className="space-y-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Provider</Label>
                <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
                  {PROVIDERS.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Webhook signing secret</Label>
                <Input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="(empty to clear)"
                />
              </div>
            </div>
            <FormError>{error}</FormError>
            <div className="flex justify-end gap-2">
              <Button type="submit" loading={save.isPending}>
                Save
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
